import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  formatPepLoginError,
  PEP_LOGIN_BUTTON_CANCEL,
  PEP_LOGIN_BUTTON_SIGN_IN,
  PEP_LOGIN_DEFAULT_URL,
  PEP_LOGIN_FOOTER,
  PEP_LOGIN_LABEL_LOGIN_URL,
  PEP_LOGIN_LABEL_PASSWORD,
  PEP_LOGIN_LABEL_USERNAME,
  PEP_LOGIN_LOADING_TEXT,
  PEP_LOGIN_PLACEHOLDER_LOGIN_URL,
  PEP_LOGIN_PLACEHOLDER_PASSWORD,
  PEP_LOGIN_PLACEHOLDER_USERNAME,
  PEP_LOGIN_SNOOZE_OPTIONS,
  PEP_LOGIN_SNOOZE_PLACEHOLDER,
  PEP_LOGIN_SNOOZE_PROMPT,
  PEP_LOGIN_SUBTITLE,
  PEP_LOGIN_TITLE,
  PEP_LOGIN_URL_TOOLTIP_LINES
} from './pepLoginCopy'

import styles from './PepLoginModal.module.scss'

export function PepLoginModal({
  isOpen,
  submitError,
  isSubmitting,
  onSubmit,
  onCancel,
  onSnooze
}) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loginURL, setLoginURL] = useState(PEP_LOGIN_DEFAULT_URL)
  const usernameRef = useRef(null)

  useEffect(() => {
    if (!isOpen) {
      setUsername('')
      setPassword('')
      setLoginURL(PEP_LOGIN_DEFAULT_URL)
      return
    }
    const timer = window.setTimeout(() => {
      usernameRef.current?.focus()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [isOpen])

  const canSubmit =
    username.trim().length > 0 &&
    password.trim().length > 0 &&
    loginURL.trim().length > 0 &&
    !isSubmitting

  const handleSubmit = useCallback(
    e => {
      e.preventDefault()
      if (!canSubmit) {
        return
      }
      onSubmit({
        username: username.trim(),
        password,
        loginURL: loginURL.trim()
      })
    },
    [canSubmit, loginURL, onSubmit, password, username]
  )

  const handleSnoozeChange = useCallback(
    e => {
      const minutes = parseInt(e.target.value, 10)
      if (!Number.isFinite(minutes) || minutes <= 0 || typeof onSnooze !== 'function') {
        return
      }
      onSnooze(minutes)
    },
    [onSnooze]
  )

  if (!isOpen) {
    return null
  }

  const showButtonGroup = !isSubmitting
  const showLoading = isSubmitting
  const showError = Boolean(submitError) && !isSubmitting
  const showLoadingPlaceholder = !isSubmitting && !submitError
  const displayError = submitError || formatPepLoginError('unknown step')

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.loginContainer}>
        <div className={styles.logo}>
          <h1 className={styles.title}>{PEP_LOGIN_TITLE}</h1>
          <p className={styles.subtitle}>{PEP_LOGIN_SUBTITLE}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="pep-username">
              {PEP_LOGIN_LABEL_USERNAME}
            </label>
            <input
              ref={usernameRef}
              id="pep-username"
              className={styles.input}
              type="text"
              placeholder={PEP_LOGIN_PLACEHOLDER_USERNAME}
              value={username}
              onChange={e => setUsername(e.target.value)}
              disabled={isSubmitting}
              autoComplete="username"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="pep-password">
              {PEP_LOGIN_LABEL_PASSWORD}
            </label>
            <input
              id="pep-password"
              className={styles.input}
              type="password"
              placeholder={PEP_LOGIN_PLACEHOLDER_PASSWORD}
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={isSubmitting}
              autoComplete="current-password"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.labelWithHelp} htmlFor="pep-login-url">
              {PEP_LOGIN_LABEL_LOGIN_URL}
              <span className={styles.helpIcon} aria-hidden="true">
                ?
                <span className={styles.tooltipContent}>
                  {PEP_LOGIN_URL_TOOLTIP_LINES.map(line => (
                    <span key={line} className={styles.tooltipLine}>
                      {line}
                    </span>
                  ))}
                </span>
              </span>
            </label>
            <input
              id="pep-login-url"
              className={styles.input}
              type="text"
              placeholder={PEP_LOGIN_PLACEHOLDER_LOGIN_URL}
              value={loginURL}
              onChange={e => setLoginURL(e.target.value)}
              disabled={isSubmitting}
              required
            />
          </div>

          <div
            className={
              showButtonGroup ? styles.buttonGroup : `${styles.buttonGroup} ${styles.hidden}`
            }
          >
            <button
              type="submit"
              className={styles.loginBtn}
              disabled={!canSubmit}
            >
              {PEP_LOGIN_BUTTON_SIGN_IN}
            </button>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {PEP_LOGIN_BUTTON_CANCEL}
            </button>
          </div>

          {typeof onSnooze === 'function' && (
            <div
              className={
                showButtonGroup
                  ? styles.snoozeGroup
                  : `${styles.snoozeGroup} ${styles.hidden}`
              }
            >
              <label className={styles.snoozeLabel} htmlFor="pep-snooze">
                {PEP_LOGIN_SNOOZE_PROMPT}
              </label>
              <select
                id="pep-snooze"
                className={styles.snoozeSelect}
                value=""
                onChange={handleSnoozeChange}
                disabled={isSubmitting}
              >
                <option value="">{PEP_LOGIN_SNOOZE_PLACEHOLDER}</option>
                {PEP_LOGIN_SNOOZE_OPTIONS.map(option => (
                  <option key={option.minutes} value={option.minutes}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div
            className={
              showLoading ? styles.loading : `${styles.loading} ${styles.hidden}`
            }
            aria-live="polite"
          >
            <div className={styles.spinner} />
            <p className={styles.loadingText}>{PEP_LOGIN_LOADING_TEXT}</p>
          </div>

          <div
            className={
              showError
                ? styles.errorMessage
                : `${styles.errorMessage} ${styles.hidden}`
            }
            role="alert"
          >
            <p className={styles.errorText}>{displayError}</p>
          </div>

          <div
            className={
              showLoadingPlaceholder
                ? styles.loadingPlaceholder
                : `${styles.loadingPlaceholder} ${styles.hidden}`
            }
            aria-hidden="true"
          />
        </form>

        <div className={styles.footer}>
          <p>{PEP_LOGIN_FOOTER}</p>
        </div>
      </div>
    </div>
  )
}

export default PepLoginModal
